import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { webAlert, webConfirm } from "../webDialog";
import { authApi, ParentProfile } from "../../api/auth";
import { Icon } from "../components/Icon";
import { WebMascot } from "../components/WebMascot";
import { ChildWithProgress } from "../WebShell";
import { WebVideo } from "../components/WebVideoCard";
import {
  adminContentApi,
  GeneratedVideo,
  GenerationStatus,
  VideoPublication,
} from "../../api/content";
import { getApiBaseUrl } from "../../api/client";
import { prettyTitle } from "../prettyTitle";
import { useAuthStore } from "../../state/authStore";
import { WebQuestionEditor } from "./WebQuestionEditor";
import { WebVideoDetailsEditor } from "./WebVideoDetailsEditor";
import {
  chatsApi,
  ChatMessageRow,
  ChatSessionSummary,
} from "../../api/chats";

// ─────────────────────────────────────────────────────────────────────────
// Kidly Studio — AI Family Content Generator (Wan-backed pipeline)
//
// The chat UI is the source of intent. The user types a prompt, optionally
// drops a reference photo, and hits Generate. The backend submits the job
// to Wan and we poll `/generation/:jobId` every 2 seconds until the job
// reaches a terminal status (`done | failed | cancelled`).
//
// Each Generation has a backend `Video.id` so the row persists across
// refreshes — `useEffect` on mount calls `listMyGenerations` to repopulate
// in-flight jobs.
// ─────────────────────────────────────────────────────────────────────────

type Role = "user" | "assistant";

type ChatMessage = {
  id: string;
  role: Role;
  text?: string;
  images?: string[]; // data URIs of uploaded photos
  generationId?: string; // links assistant message to a generation in progress
};

// One row in the Studio's gen-card list. `id` is the backend Video.id and
// `jobId` is the Wan job_id (same value as `Video.generationJobId`). The
// non-Wan visual fields (`imageCount`, `thumbColor`) are local-only and
// drive the gen-card art.
type Generation = {
  id: string; // backend Video.id (or a temp uid before the POST resolves)
  jobId: string; // Wan job_id; same as generationJobId
  // True when the parent uploaded their own MP4 instead of AI-generating it
  // (backend `generationJobId` is null → no Wan stills to pull). Drives the
  // question editor to offer image Upload only, never canvas frame-extraction.
  isUploaded?: boolean;
  prompt: string;
  imageCount: number;
  status: GenerationStatus;
  title?: string;
  // Parent-authored long-form copy. Empty until they finalize the video.
  description?: string | null;
  durationSec?: number;
  videoUrl?: string; // hlsUrl from backend once `done`
  // Persisted poster URL — either a hosted /v1/videos/:id/thumb path or
  // an external URL for seed content. Empty when the parent hasn't
  // uploaded one yet (FE falls back to procedural card art).
  thumbUrl?: string;
  errorMessage?: string;
  thumbColor?: string;
  // Parent-editable metadata. Defaults match the generation service.
  categories?: string[];
  ageMin?: number;
  ageMax?: number;
  createdAt: number;
  // In the public free library — guests can browse + play it without an
  // account. Only an admin can set this (see `reviewStatus`).
  isPublic?: boolean;
  // Where this video sits in the admin-review flow for going public:
  // 'none' (never asked) | 'pending' (waiting on admin, still private) |
  // 'approved' (public) | 'rejected' (declined; `reviewNote` says why).
  reviewStatus?: "none" | "pending" | "approved" | "rejected";
  reviewNote?: string | null;
  // Which of the parent's kids this video has been published for. Drives
  // the "Published for Leo, Mila" badge and pre-fills the publish modal's
  // checkbox state. `undefined` (not `[]`) means we don't have the join
  // data yet — happens right after the optimistic /generate response.
  publications?: VideoPublication[];
};

// Bucket ratios — Wan's per-shot progress isn't surfaced through the
// backend yet, so we map status → ratio for a steady-feeling bar.
const STATUS_RATIO: Record<GenerationStatus, number> = {
  queued: 0.1,
  moderating: 0.2,
  generating: 0.6,
  stitching: 0.85,
  done: 1.0,
  failed: 1.0,
  cancelled: 1.0,
};

const GEN_STATUS_LABEL: Record<GenerationStatus, string> = {
  queued: "Warming up the crayons",
  moderating: "Checking it's kid-safe",
  generating: "Painting characters",
  stitching: "Stitching scenes together",
  done: "Ready",
  failed: "Couldn't finish — try a new idea",
  cancelled: "Cancelled",
};

const TERMINAL_STATUSES: GenerationStatus[] = ["done", "failed", "cancelled"];

// Failed/cancelled cards have no upstream job left to act on, so the only
// sensible action is to clear them from the chat. We remember which the parent
// dismissed in localStorage so they stay gone across reloads — purely cosmetic,
// the underlying Video rows are never touched.
const DISMISSED_FAILED_KEY = "mapa.studio.dismissedFailedGens";

const PALETTE = ["#FFB774", "#5B8DEF", "#F18EB6", "#7AC74F", "#8C7DD9"];

// Style + duration pickers exposed in the composer. Style IDs must match
// Wan's /styles catalog (backend allow-lists them; unknown values fall back
// to 'storybook'). Duration is clamped server-side to 5-30s.
const STYLE_OPTIONS: { id: string; label: string; tooltip: string }[] = [
  {
    id: "storybook",
    label: "Storybook",
    tooltip: "Children's-book watercolor, soft pastel, calm.",
  },
  {
    id: "3d_cartoon",
    label: "3D Cartoon",
    tooltip: "Pixar-style 3D animation with polished CGI and soft lighting.",
  },
  {
    id: "2d_cartoon",
    label: "2D Cartoon",
    tooltip: "Classic Looney-Tunes-style 2D cel animation, bold outlines.",
  },
  {
    id: "claymation",
    label: "Claymation",
    tooltip: "Wallace & Gromit-style stop-motion with handcrafted textures.",
  },
];

const DURATION_OPTIONS = [5, 10, 15, 20, 30];

const STARTERS = [
  "Counting cookies with Grandma",
  "Bedtime story about the moon and stars",
  "Brushing teeth with Dad",
  "Sharing toys at the playground",
  "Learning the letter B with Mim",
];

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function readFiles(files: FileList | null): Promise<string[]> {
  if (!files || files.length === 0) return Promise.resolve([]);
  const max = Math.min(files.length, 6);
  const items = Array.from(files).slice(0, max);
  return Promise.all(
    items.map(
      (f) =>
        new Promise<string>((resolve) => {
          if (!f.type.startsWith("image/")) return resolve("");
          const r = new FileReader();
          r.onload = () => resolve(String(r.result ?? ""));
          r.onerror = () => resolve("");
          r.readAsDataURL(f);
        }),
    ),
  ).then((arr) => arr.filter(Boolean));
}

function fmtTime(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Map a backend GeneratedVideo into the local gen-card shape. Picks a
// stable thumb color per row from a deterministic id hash so the card
// doesn't repaint on every refresh.
function pickThumb(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function backendToGeneration(v: GeneratedVideo): Generation {
  return {
    id: v.id,
    jobId: v.generationJobId ?? v.id,
    // No Wan job id ⇒ this is a parent upload (within Studio every video is
    // either the parent's generation or their upload; seed rows aren't here).
    isUploaded: !v.generationJobId,
    isPublic: v.isPublic,
    reviewStatus: v.reviewStatus ?? "none",
    reviewNote: v.reviewNote ?? null,
    prompt: v.generationPrompt ?? "",
    imageCount: 0, // backend doesn't echo this; visual fallback only
    status: v.generationStatus,
    title: v.title,
    description: v.description ?? null,
    durationSec: v.duration || undefined,
    videoUrl: v.hlsUrl || undefined,
    thumbUrl: v.thumbUrl || undefined,
    categories: v.categories ?? [],
    ageMin: v.ageMin,
    ageMax: v.ageMax,
    errorMessage: v.generationError ?? undefined,
    thumbColor: pickThumb(v.id),
    createdAt: v.createdAt ? new Date(v.createdAt).getTime() : Date.now(),
    // Carry the publication join when the endpoint provides it. The
    // immediate /generate response doesn't include it — that path keeps
    // `publications` undefined until the next poll/refresh fills it in.
    publications: v.publications,
  };
}

export function WebStudio({
  activeChild,
  children: kids,
  onPlay,
}: {
  activeChild: ChildWithProgress | null;
  // Full family roster — used by the "Publish for…" modal so the parent can
  // pick which kid each generation goes to. Falls back to `[activeChild]`
  // if WebShell hasn't loaded the list yet, so the modal still works
  // single-kid out of the gate.
  children?: ChildWithProgress[];
  onPlay?: (video: WebVideo) => void;
}) {
  const { accessToken } = useAuthStore();
  // ── Chat session state ────────────────────────────────────────────────
  // `sessions`        : sidebar listing (no message bodies), sorted by
  //                     updatedAt desc.
  // `activeSessionId` : which session is open in the main pane. `null`
  //                     means "draft" — nothing persisted yet. Persists
  //                     lazily on the first user message.
  // `messages`        : in-memory message buffer for the active session.
  //                     Hydrated from the backend when activeSessionId
  //                     changes; mutated locally as new messages land
  //                     so the chat feels instant.
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  // Sidebar visibility — hidden by default to keep Studio focused on the
  // active chat. Toggled from a hamburger button in the chat header; the
  // sidebar slides in from the left as an absolute-positioned overlay
  // (doesn't reflow the chat or library panes).
  const [sessionsOpen, setSessionsOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: uid(),
      role: "assistant",
      text:
        activeChild
          ? `Hi! I'm Mim, ${activeChild.name}'s storymaker. Describe a cartoon you want for ${activeChild.name}, and I'll make it.`
          : "Hi! I'm Mim, your family storymaker. Describe the cartoon you want and I'll make it.",
    },
  ]);
  // Refs so async handlers (send, polling) can read the latest values
  // without re-binding on every render.
  const activeSessionIdRef = useRef<string | null>(null);
  activeSessionIdRef.current = activeSessionId;
  const [draft, setDraft] = useState("");
  const [draftImages, setDraftImages] = useState<string[]>([]);
  // Per-prompt generation prefs. Defaults match the backend defaults so the
  // first generation behaves the same as before this picker existed.
  const [style, setStyle] = useState<string>("storybook");
  const [duration, setDuration] = useState<number>(15);
  const [extendPrompt, setExtendPrompt] = useState<boolean>(true);
  const [generations, setGenerations] = useState<Generation[]>([]);
  // jobId currently being cancelled — drives the button's pending state.
  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);
  // Generation ids whose failed/cancelled card the parent has cleared from the
  // chat. Lazy-loaded from localStorage so dismissals survive a refresh.
  const [dismissedFailedIds, setDismissedFailedIds] = useState<Set<string>>(
    () => {
      if (typeof window === "undefined") return new Set();
      try {
        const raw = window.localStorage.getItem(DISMISSED_FAILED_KEY);
        return new Set<string>(raw ? JSON.parse(raw) : []);
      } catch {
        return new Set();
      }
    },
  );
  const dismissFailedCard = useCallback((id: string) => {
    setDismissedFailedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            DISMISSED_FAILED_KEY,
            JSON.stringify(Array.from(next)),
          );
        } catch {
          /* ignore quota / disabled storage */
        }
      }
      return next;
    });
  }, []);

  // Archive a finished video — soft-delete: it's hidden from the library, kids,
  // and the public catalog, and auto-deleted after the retention window.
  // Removed from the local list on success so it disappears immediately.
  const handleArchive = useCallback(
    async (id: string) => {
      if (!accessToken) return;
      const ok = await webConfirm(
        "Archive this video? It's hidden everywhere now and permanently deleted in 10 days — restorable until then.",
        { confirmLabel: "Archive", destructive: true },
      );
      if (!ok) return;
      try {
        await adminContentApi.archiveVideo(id, accessToken);
        setGenerations((prev) => prev.filter((g) => g.id !== id));
      } catch (e) {
        webAlert(
          e instanceof Error ? e.message : "Couldn't archive — try again.",
        );
      }
    },
    [accessToken],
  );
  const [isDragging, setIsDragging] = useState(false);
  const [openGen, setOpenGen] = useState<string | null>(null);
  // Generation IDs the parent explicitly closed. We never auto-re-open
  // a modal that's been dismissed once — eliminates the "I clicked X but
  // it came back" race that polling can cause while the same job is
  // still emitting `done` updates.
  const dismissedGensRef = useRef<Set<string>>(new Set());
  // ID of the generation whose "Publish for…" modal is currently open. The
  // modal pulls the parent's kid list from the `kids` prop and pre-checks
  // any childIds already in that generation's `publications` array.
  const [publishGenId, setPublishGenId] = useState<string | null>(null);
  // ID of the generation whose "Edit questions" modal is currently open.
  // Mutually exclusive with `openGen` (preview) and `publishGenId` (publish).
  const [questionsGenId, setQuestionsGenId] = useState<string | null>(null);
  // The kid list available to the publish modal. Falls back to the single
  // active kid when WebShell hasn't passed the full roster (older callers).
  const publishableKids: ChildWithProgress[] = useMemo(() => {
    if (kids && kids.length > 0) return kids;
    return activeChild ? [activeChild] : [];
  }, [kids, activeChild]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  // Upload a finished video instead of generating one. Reads the file as a
  // data URL and POSTs it; the new row lands in the library like a done gen.
  const onVideoSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // allow re-picking the same file
      if (!file || !accessToken) return;
      if (file.size > 40 * 1024 * 1024) {
        await webAlert(
          "That file is over 40 MB — please upload a shorter clip.",
        );
        return;
      }
      setUploadingVideo(true);
      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result));
          r.onerror = () => reject(r.error);
          r.readAsDataURL(file);
        });
        const title =
          file.name
            .replace(/\.[^.]+$/, "")
            .replace(/_+/g, " ")
            .replace(/\s+/g, " ")
            .trim() || "My video";
        const created = await adminContentApi.uploadVideo(
          { dataUrl, title },
          accessToken,
        );
        setGenerations((prev) => [backendToGeneration(created), ...prev]);
      } catch (err) {
        await webAlert(
          err instanceof Error ? err.message : "Couldn't upload the video.",
        );
      } finally {
        setUploadingVideo(false);
      }
    },
    [accessToken],
  );
  // Ref to the chat composer so Iterate (and other "seed the prompt"
  // actions) can scroll the textarea into view + drop the caret at the
  // end of the seeded text. Without this the user clicks Iterate, the
  // modal closes, and they don't notice the prompt got pre-filled below.
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const generationsRef = useRef<Generation[]>([]);
  generationsRef.current = generations;

  // True while the user is parked at (or near) the bottom of the chat. We only
  // auto-scroll on new content when this holds — so scrolling up to re-read a
  // long message isn't yanked back down by the ~2s generation-status poll.
  const stickToBottomRef = useRef(true);
  const onScrollerScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  // Keep the chat pinned to the bottom as new content arrives — but ONLY when
  // the user is already there. If they've scrolled up, don't fight them (the
  // poll updates `generations` every couple seconds, which used to yank the
  // view back down on every tick).
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, generations]);

  // ── Real Wan-backed generation pipeline ───────────────────────────────
  //
  // Lifecycle:
  // 1. `send()` optimistically pushes a temp Generation `{status:'queued'}`
  //    so the UI feels instant, then POSTs to the backend's /generate proxy.
  // 2. On the 202 response we have a real `videoId` + `jobId`. The temp row
  //    is replaced with the backend Generation.
  // 3. `startPolling(jobId)` kicks off a 2s interval against
  //    /generation/:jobId. The backend (a) throttles tab-fanout via its
  //    1.5s cache and (b) short-circuits once the row is terminal.
  // 4. On a terminal status the interval is cleared and the assistant
  //    posts a closing chat message.
  //
  // Polling state lives in a ref (not state) so re-renders don't restart
  // intervals. The Map is keyed by jobId so multiple in-flight generations
  // each get their own loop.
  const pollersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map(),
  );

  const stopPolling = useCallback((jobId: string) => {
    const t = pollersRef.current.get(jobId);
    if (t !== undefined) {
      clearInterval(t);
      pollersRef.current.delete(jobId);
    }
  }, []);

  // Cancel an in-flight Wan generation — ported from the native StudioScreen,
  // which had this all along; the web Studio just never wired it up. Stops the
  // job upstream, kills the local poller, and flips the card to "cancelled".
  const handleCancelGeneration = useCallback(
    async (jobId: string) => {
      if (!accessToken || !jobId || cancellingJobId) return;
      setCancellingJobId(jobId);
      try {
        await adminContentApi.cancelGeneration(jobId, accessToken);
        stopPolling(jobId);
        setGenerations((prev) =>
          prev.map((g) =>
            g.jobId === jobId ? { ...g, status: "cancelled" } : g,
          ),
        );
      } catch (e) {
        webAlert(
          e instanceof Error ? e.message : "Couldn't cancel — try again.",
        );
      } finally {
        setCancellingJobId(null);
      }
    },
    [accessToken, cancellingJobId, stopPolling],
  );

  // Helper: refresh the sidebar listing. Defined here (before
  // applyServerUpdate) so the message-persist helper that the polling
  // callbacks reference is in scope.
  const refreshSessions = useCallback(async () => {
    if (!accessToken) return;
    try {
      const list = await chatsApi.list(accessToken);
      setSessions(list);
    } catch {
      // Sidebar shows what it has; the next interaction will retry.
    }
  }, [accessToken]);

  // Persist a message to the backend. Best-effort: if it fails (network,
  // 401), the UI keeps the in-memory copy and the next send tries again.
  const persistMessage = useCallback(
    async (
      sessionId: string,
      body: {
        role: "user" | "assistant";
        text?: string;
        images?: string[];
        generationId?: string;
      },
    ) => {
      if (!accessToken) return;
      try {
        await chatsApi.appendMessage(
          sessionId,
          {
            role: body.role,
            text: body.text ?? null,
            images: body.images && body.images.length > 0 ? body.images : null,
            generationId: body.generationId ?? null,
          },
          accessToken,
        );
        refreshSessions();
      } catch {
        // Swallow — the in-memory copy is still rendered.
      }
    },
    [accessToken, refreshSessions],
  );

  const applyServerUpdate = useCallback(
    (v: GeneratedVideo) => {
      const incoming = backendToGeneration(v);
      // Track whether THIS update is the moment a generation flips to
      // `done` (so we can auto-open the finalize modal exactly once,
      // instead of every subsequent poll that also returns done).
      let justTransitionedToDone = false;
      setGenerations((prev) => {
        const previous = prev.find((g) => g.jobId === incoming.jobId);
        if (
          v.generationStatus === "done" &&
          previous &&
          previous.status !== "done"
        ) {
          justTransitionedToDone = true;
        }
        return prev.map((g) =>
          g.jobId === incoming.jobId
            ? { ...g, ...incoming, thumbColor: g.thumbColor ?? incoming.thumbColor }
            : g,
        );
      });
      if (
        justTransitionedToDone &&
        !dismissedGensRef.current.has(incoming.id)
      ) {
        // Drop the parent straight into the rich finalize editor so they
        // can name the video, write a description, pick a poster, and add
        // questions before they walk away from the screen. Skipped if the
        // parent has already dismissed this generation's modal once —
        // closing should stay closed.
        setOpenGen(incoming.id);
      }
      if (TERMINAL_STATUSES.includes(v.generationStatus)) {
        stopPolling(incoming.jobId);
        const sid = activeSessionIdRef.current;
        if (v.generationStatus === "done") {
          const doneText = `Done! Your cartoon **"${incoming.title ?? "Your custom cartoon"}"** is ready. I opened the editor so you can name it, add a poster, and write questions.`;
          setMessages((prev) => [
            ...prev,
            {
              id: uid(),
              role: "assistant",
              text: doneText,
              generationId: incoming.id,
            },
          ]);
          if (sid) {
            persistMessage(sid, {
              role: "assistant",
              text: doneText,
              generationId: incoming.id,
            });
          }
        } else if (v.generationStatus === "failed") {
          const failText =
            v.generationError && v.generationError.toLowerCase().includes("moderation")
              ? "Hmm — that idea didn't pass kid-safety. Try a different prompt and I'll try again."
              : "Couldn't finish that one. Try a different idea and I'll have another go.";
          setMessages((prev) => [
            ...prev,
            {
              id: uid(),
              role: "assistant",
              text: failText,
              generationId: incoming.id,
            },
          ]);
          if (sid) {
            persistMessage(sid, {
              role: "assistant",
              text: failText,
              generationId: incoming.id,
            });
          }
        }
      }
    },
    [stopPolling, persistMessage],
  );

  const startPolling = useCallback(
    (jobId: string) => {
      if (!accessToken) return;
      if (pollersRef.current.has(jobId)) return;
      const tick = async () => {
        try {
          const v = await adminContentApi.getGeneration(jobId, accessToken);
          applyServerUpdate(v);
        } catch {
          // Transient network error — keep the loop alive; the next tick
          // will retry. Backend already throttles its calls to Wan.
        }
      };
      const id = setInterval(tick, 2000);
      pollersRef.current.set(jobId, id);
      // Kick once immediately so the UI doesn't sit on `queued` for 2s.
      tick();
    },
    [accessToken, applyServerUpdate],
  );

  // Hydrate on mount — pulls the parent's own generations so refreshing
  // mid-job restores the in-flight cards and resumes polling.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    adminContentApi
      .listMyGenerations(accessToken)
      .then((rows) => {
        if (cancelled) return;
        const seeded = rows.map(backendToGeneration);
        setGenerations(seeded);
        for (const g of seeded) {
          if (!TERMINAL_STATUSES.includes(g.status)) startPolling(g.jobId);
        }
      })
      .catch(() => {
        // Empty list is fine; missing endpoint surfaces as no rows.
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, startPolling]);

  // Cleanup all pollers on unmount.
  useEffect(() => {
    const map = pollersRef.current;
    return () => {
      for (const t of map.values()) clearInterval(t);
      map.clear();
    };
  }, []);

  // Hydrate sessions on mount. We don't auto-open the most-recent session
  // — landing in a fresh "draft" state matches the user's mental model of
  // "start typing → it becomes a session". The sidebar lets them jump
  // into past sessions when they want. Pulls via the helper defined above
  // (which is also called after every persisted message).
  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  // When the parent switches to an existing session, hydrate its messages
  // from the backend. Persisted messages carry `images` and
  // `generationId`; we map both back into the local ChatMessage shape so
  // the gen-card progress bubble still binds to the right Generation.
  //
  // No synthetic welcome is prepended here. Real sessions always open
  // with the parent's first user message; prepending the Mim greeting
  // produced a confusing duplicate-welcome on every reload. The fresh-
  // draft state (handleNewChat + initial useState) is where the welcome
  // belongs — it primes empty conversations.
  const loadSession = useCallback(
    async (sessionId: string) => {
      if (!accessToken) return;
      setActiveSessionId(sessionId);
      try {
        const session = await chatsApi.get(sessionId, accessToken);
        const hydrated: ChatMessage[] = session.messages.map(
          (m: ChatMessageRow): ChatMessage => ({
            id: m.id,
            role: m.role,
            text: m.text ?? undefined,
            images: m.images && m.images.length > 0 ? m.images : undefined,
            generationId: m.generationId ?? undefined,
          }),
        );
        setMessages(hydrated);
      } catch {
        // Session disappeared (deleted from another tab, etc.) — reset to
        // a fresh draft.
        setActiveSessionId(null);
      }
    },
    [accessToken],
  );

  // Lazy-create the active session on first user send. Returns the id of
  // the session to write into (existing or freshly-created). Caller
  // captures it before posting messages so concurrent sends don't race
  // each other on the create step.
  const ensureActiveSession = useCallback(async (): Promise<string | null> => {
    if (!accessToken) return null;
    if (activeSessionIdRef.current) return activeSessionIdRef.current;
    try {
      const created = await chatsApi.create({}, accessToken);
      setActiveSessionId(created.id);
      activeSessionIdRef.current = created.id;
      // Refresh sidebar so the new session shows up immediately.
      refreshSessions();
      return created.id;
    } catch {
      return null;
    }
  }, [accessToken, refreshSessions]);

  // Sidebar actions — wrapped for the JSX so the buttons can call them
  // directly. Each one keeps local sidebar state in sync optimistically.
  const handleNewChat = useCallback(() => {
    setActiveSessionId(null);
    activeSessionIdRef.current = null;
    setMessages([
      {
        id: uid(),
        role: "assistant",
        text:
          activeChild
            ? `Hi! I'm Mim, ${activeChild.name}'s storymaker. Describe a cartoon you want for ${activeChild.name}, and I'll make it.`
            : "Hi! I'm Mim, your family storymaker. Describe the cartoon you want and I'll make it.",
      },
    ]);
    setDraft("");
    setDraftImages([]);
  }, [activeChild]);

  const handleRenameSession = useCallback(
    async (sessionId: string) => {
      if (!accessToken) return;
      const current = sessions.find((s) => s.id === sessionId);
      const next = window.prompt("Rename chat", current?.title ?? "");
      if (!next || !next.trim()) return;
      try {
        await chatsApi.rename(sessionId, next.trim(), accessToken);
        refreshSessions();
      } catch (e) {
        webAlert(e instanceof Error ? e.message : "Couldn't rename.");
      }
    },
    [accessToken, sessions, refreshSessions],
  );

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      if (!accessToken) return;
      const ok = await webConfirm("Delete this chat? The conversation is gone — generated videos stay in your library.", { confirmLabel: "Confirm", destructive: true });
      if (!ok) return;
      try {
        await chatsApi.remove(sessionId, accessToken);
        refreshSessions();
        if (activeSessionIdRef.current === sessionId) {
          handleNewChat();
        }
      } catch (e) {
        webAlert(e instanceof Error ? e.message : "Couldn't delete.");
      }
    },
    [accessToken, refreshSessions, handleNewChat],
  );

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text && draftImages.length === 0) return;
    if (!accessToken) return;

    // Sending always re-pins to the bottom so the new message + reply are
    // visible, even if the user had scrolled up to read earlier in the thread.
    stickToBottomRef.current = true;

    const userMsg: ChatMessage = {
      id: uid(),
      role: "user",
      text: text || undefined,
      images: draftImages.length > 0 ? draftImages : undefined,
    };
    const tempId = `tmp-${uid()}`;
    const ackText = (() => {
      if (text)
        return `On it — making "${text}". This can take a few minutes — feel free to keep chatting.`;
      // Reached only when the parent attached images and typed nothing. The
      // generator needs a prompt, so ask for one rather than implying the
      // images alone will drive the video.
      return "Add a line about what should happen and I'll start making it.";
    })();
    const ackMsg: ChatMessage = {
      id: uid(),
      role: "assistant",
      text: ackText,
      generationId: tempId,
    };

    const tempGen: Generation = {
      id: tempId,
      jobId: tempId,
      prompt: text || `Photo-based cartoon (${draftImages.length} images)`,
      imageCount: draftImages.length,
      status: "queued",
      thumbColor: pickThumb(tempId),
      createdAt: Date.now(),
    };

    setMessages((prev) => [...prev, userMsg, ackMsg]);
    setGenerations((prev) => [tempGen, ...prev]);
    const submittedPrompt = text || "A family cartoon";
    const photo = draftImages[0]; // Wan accepts ONE reference_image
    setDraft("");
    setDraftImages([]);

    // Lazy-persist the chat session: create on first send, then write the
    // user message + the assistant ack. The ack's generationId starts as a
    // temp id and gets rewritten after the backend returns the real Video.
    const sessionId = await ensureActiveSession();
    if (sessionId) {
      // Fire-and-forget — the in-memory copy is already on screen.
      persistMessage(sessionId, {
        role: "user",
        text: text || undefined,
        images: draftImages.length > 0 ? draftImages : undefined,
      });
      // Ack message is persisted after we know the real generation id
      // (below) so the saved row links to the right Video.
    }

    try {
      const created = await adminContentApi.generateVideo(
        {
          prompt: submittedPrompt,
          photoDataUrl: photo,
          duration,
          style,
          extendPrompt,
        },
        accessToken,
      );
      const real = backendToGeneration(created);
      // Carry forward the optimistic imageCount/thumbColor so the card doesn't
      // visually reset when the backend ID arrives.
      const merged: Generation = {
        ...real,
        imageCount: tempGen.imageCount,
        thumbColor: tempGen.thumbColor ?? real.thumbColor,
      };
      setGenerations((prev) =>
        prev.map((g) => (g.id === tempId ? merged : g)),
      );
      // Re-link the ack message to the real backend id.
      setMessages((prev) =>
        prev.map((m) =>
          m.generationId === tempId ? { ...m, generationId: real.id } : m,
        ),
      );
      // Persist the ack now that we have the real id to bind to.
      if (sessionId) {
        persistMessage(sessionId, {
          role: "assistant",
          text: ackText,
          generationId: real.id,
        });
      }
      startPolling(real.jobId);
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : "Something went wrong.";
      setGenerations((prev) =>
        prev.map((g) =>
          g.id === tempId
            ? { ...g, status: "failed", errorMessage: errMsg }
            : g,
        ),
      );
      const errText = `Couldn't kick that off — ${errMsg}`;
      setMessages((prev) => [
        ...prev,
        {
          id: uid(),
          role: "assistant",
          text: errText,
          generationId: tempId,
        },
      ]);
      if (sessionId) {
        persistMessage(sessionId, { role: "assistant", text: errText });
      }
    }
  }, [
    draft,
    draftImages,
    duration,
    style,
    extendPrompt,
    accessToken,
    startPolling,
    ensureActiveSession,
    persistMessage,
  ]);

  const onUploadClick = () => fileInputRef.current?.click();
  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const imgs = await readFiles(e.target.files);
    if (imgs.length > 0) {
      setDraftImages((prev) => [...prev, ...imgs].slice(0, 6));
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const imgs = await readFiles(e.dataTransfer.files);
    if (imgs.length > 0) {
      setDraftImages((prev) => [...prev, ...imgs].slice(0, 6));
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const currentGen = useMemo(
    () => generations.find((g) => g.id === openGen) ?? null,
    [generations, openGen],
  );
  const publishGen = useMemo(
    () => generations.find((g) => g.id === publishGenId) ?? null,
    [generations, publishGenId],
  );
  const questionsGen = useMemo(
    () => generations.find((g) => g.id === questionsGenId) ?? null,
    [generations, questionsGenId],
  );

  // Merge a publish-endpoint response back into local state. The backend
  // returns the whole Video row including the updated `publications` join,
  // so we patch just that field — the rest of the gen-card (title, thumb,
  // status) hasn't changed and we want to avoid a visual flicker.
  const applyPublications = useCallback(
    (videoId: string, publications: VideoPublication[]) => {
      setGenerations((prev) =>
        prev.map((g) => (g.id === videoId ? { ...g, publications } : g)),
      );
    },
    [],
  );

  // Going public is admin-reviewed. A parent submits (video stays private and
  // goes into the admin's queue) and can withdraw while it's still pending;
  // only an admin's approval sets `isPublic`. The admin, in turn, gets the
  // direct toggle here since their own action IS the review.
  const [publicBusyId, setPublicBusyId] = useState<string | null>(null);
  // Who's looking: the admin reviews videos, a parent submits them.
  const [profile, setProfile] = useState<ParentProfile | null>(null);
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    authApi
      .me(accessToken)
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch(() => {
        /* non-fatal: fall back to the parent (submit-for-review) flow */
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);
  const isAdmin = !!profile?.isSuperAdmin;

  const applyUpdated = useCallback((v: GeneratedVideo) => {
    setGenerations((prev) =>
      prev.map((x) =>
        x.id === v.id
          ? {
              ...x,
              isPublic: v.isPublic,
              reviewStatus: v.reviewStatus ?? "none",
              reviewNote: v.reviewNote ?? null,
            }
          : x,
      ),
    );
  }, []);

  // Admin-only: straight in/out of the free library.
  const togglePublic = useCallback(
    async (g: Generation) => {
      if (!accessToken || publicBusyId) return;
      const next = !g.isPublic;
      setPublicBusyId(g.id);
      try {
        const updated = next
          ? await adminContentApi.approveReview(g.id, accessToken)
          : await adminContentApi.updateVideo(
              g.id,
              { isPublic: false },
              accessToken,
            );
        applyUpdated(updated);
      } catch (e) {
        webAlert(
          e instanceof Error ? e.message : "Couldn't change visibility.",
        );
      } finally {
        setPublicBusyId(null);
      }
    },
    [accessToken, publicBusyId, applyUpdated],
  );

  // Parent-side: ask an admin to publish, or take the request back.
  const toggleReview = useCallback(
    async (g: Generation) => {
      if (!accessToken || publicBusyId) return;
      const pending = g.reviewStatus === "pending";
      if (!pending) {
        const ok = await webConfirm(
          "Send this video to Kidly for review? An admin checks it before other families can watch. It stays private until then.",
          { confirmLabel: "Send for review" },
        );
        if (!ok) return;
      }
      setPublicBusyId(g.id);
      try {
        const updated = pending
          ? await adminContentApi.withdrawReview(g.id, accessToken)
          : await adminContentApi.submitForReview(g.id, accessToken);
        applyUpdated(updated);
      } catch (e) {
        webAlert(e instanceof Error ? e.message : "Couldn't submit for review.");
      } finally {
        setPublicBusyId(null);
      }
    },
    [accessToken, publicBusyId, applyUpdated],
  );

  return (
    <div className="ws-root">
      {/* Backdrop — only rendered when the sidebar is open. Click to close. */}
      {sessionsOpen && (
        <div
          className="ws-sessions-backdrop"
          onClick={() => setSessionsOpen(false)}
        />
      )}

      {/* Sessions sidebar — absolute-positioned overlay sliding in from
          the left. `open` class drives the transform so the panel
          animates in/out instead of popping. */}
      <aside
        className={"ws-sessions " + (sessionsOpen ? "open" : "")}
        aria-hidden={!sessionsOpen}
      >
        <div className="ws-sessions-head">
          <span className="ws-sessions-title">Chats</span>
          <button
            type="button"
            className="ws-sessions-close"
            onClick={() => setSessionsOpen(false)}
            aria-label="Close chats sidebar"
          >
            ×
          </button>
        </div>
        <button
          className="ws-sessions-new"
          onClick={() => {
            handleNewChat();
            setSessionsOpen(false);
          }}
        >
          <Icon name="plus" size={14} /> New chat
        </button>
        <div className="ws-sessions-list">
          {sessions.length === 0 ? (
            <div className="ws-sessions-empty">
              Your chats will appear here.
            </div>
          ) : (
            sessions.map((s) => {
              const isActive = activeSessionId === s.id;
              return (
                <div
                  key={s.id}
                  className={"ws-session-row " + (isActive ? "active" : "")}
                >
                  <button
                    className="ws-session-main"
                    onClick={() => {
                      if (!isActive) loadSession(s.id);
                      setSessionsOpen(false);
                    }}
                    title={s.title}
                  >
                    <div className="ws-session-title">{s.title}</div>
                    <div className="ws-session-sub">
                      {s._count.messages}{" "}
                      {s._count.messages === 1 ? "message" : "messages"}
                    </div>
                  </button>
                  <div className="ws-session-actions">
                    <button
                      type="button"
                      className="ws-session-act"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRenameSession(s.id);
                      }}
                      title="Rename"
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="ws-session-act ws-session-del"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession(s.id);
                      }}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* Left (now main): chat thread */}
      <div className="ws-thread">
        <div className="ws-header">
          <div className="ws-header-left">
            <button
              type="button"
              className="ws-hamburger"
              onClick={() => setSessionsOpen((v) => !v)}
              aria-label="Open chats"
              title="Open chats"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 6h16M4 12h16M4 18h16"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div>
              <div className="ws-eyebrow">KIDLY STUDIO · BETA</div>
              <div className="ws-title">Make a cartoon for your family</div>
            </div>
          </div>
          <button className="ws-newbtn" onClick={handleNewChat}>
            <Icon name="plus" size={14} /> New chat
          </button>
        </div>

        <div
          className="ws-scroller"
          ref={scrollerRef}
          onScroll={onScrollerScroll}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
        >
          {messages.length === 1 && messages[0].role === "assistant" && (
            <div className="ws-starters">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  className="ws-starter"
                  onClick={() => setDraft(s)}
                >
                  <Icon name="sparkle" size={14} /> {s}
                </button>
              ))}
            </div>
          )}

          {messages.map((m) => {
            const gen = m.generationId
              ? generations.find((g) => g.id === m.generationId)
              : null;
            return (
              <div
                key={m.id}
                className={`ws-msg ${m.role === "user" ? "ws-msg-user" : "ws-msg-asst"}`}
              >
                {m.role === "assistant" && (
                  <div className="ws-avatar">
                    <WebMascot mood="happy" color="#FF8A4C" size={26} />
                  </div>
                )}
                <div className="ws-bubble">
                  {m.images && m.images.length > 0 && (
                    <div className="ws-imgs">
                      {m.images.map((src, i) => (
                        <img key={i} src={src} alt="" />
                      ))}
                    </div>
                  )}
                  {m.text && <div className="ws-text">{renderMarkdown(m.text)}</div>}

                  {gen && !TERMINAL_STATUSES.includes(gen.status) && (
                    <div className="ws-progress">
                      <div className="ws-progress-row">
                        <Icon name="sparkle" size={14} />
                        <span>{GEN_STATUS_LABEL[gen.status]}</span>
                        <button
                          onClick={() => handleCancelGeneration(gen.jobId)}
                          disabled={cancellingJobId === gen.jobId}
                          title="Cancel this generation"
                          style={{
                            marginLeft: "auto",
                            border: "none",
                            background: "transparent",
                            color: "var(--ink-2)",
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: "pointer",
                            padding: "2px 6px",
                          }}
                        >
                          {cancellingJobId === gen.jobId
                            ? "Cancelling…"
                            : "Cancel"}
                        </button>
                      </div>
                      <div className="ws-progress-bg">
                        <div
                          className="ws-progress-fill"
                          style={{
                            width: `${STATUS_RATIO[gen.status] * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {gen && gen.status === "done" && (
                    <button
                      className="ws-gen-card"
                      onClick={() => {
                        // Explicit re-open clears the dismissal so the
                        // auto-open + close-respect logic isn't sticky.
                        dismissedGensRef.current.delete(gen.id);
                        setOpenGen(gen.id);
                      }}
                    >
                      <div
                        className="ws-gen-thumb"
                        style={{
                          background: `linear-gradient(135deg, ${gen.thumbColor ?? "#FFB774"}, #FF8A4C)`,
                          position: "relative",
                        }}
                      >
                        <WebMascot mood="cheer" color="#fff" size={48} />
                        {gen.thumbUrl && (
                          <img
                            src={
                              gen.thumbUrl.startsWith("http")
                                ? gen.thumbUrl
                                : `${getApiBaseUrl()}${gen.thumbUrl}`
                            }
                            alt=""
                            onError={(e) => {
                              // Hide the broken poster so the gradient +
                              // mascot fallback remains visible.
                              (e.currentTarget as HTMLImageElement).style.display =
                                "none";
                            }}
                            style={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        )}
                      </div>
                      <div className="ws-gen-meta">
                        <div className="ws-gen-cat">FAMILY · CUSTOM</div>
                        <div className="ws-gen-title">{prettyTitle(gen.title)}</div>
                        <div className="ws-gen-sub">
                          {fmtTime(gen.durationSec ?? 15)} · ready to play
                        </div>
                      </div>
                      <Icon name="play" size={20} />
                    </button>
                  )}

                  {gen &&
                    (gen.status === "failed" || gen.status === "cancelled") &&
                    !dismissedFailedIds.has(gen.id) && (
                    <div className="ws-progress" style={{ background: "var(--bg-warm)" }}>
                      <div className="ws-progress-row">
                        <Icon name="sparkle" size={14} />
                        <span>{GEN_STATUS_LABEL[gen.status]}</span>
                        <button
                          onClick={() => dismissFailedCard(gen.id)}
                          title="Clear this card"
                          style={{
                            marginLeft: "auto",
                            border: "none",
                            background: "transparent",
                            color: "var(--ink-2)",
                            fontWeight: 700,
                            fontSize: 16,
                            lineHeight: 1,
                            cursor: "pointer",
                            padding: "0 4px",
                          }}
                        >
                          ×
                        </button>
                      </div>
                      {gen.errorMessage && (
                        <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 6 }}>
                          {gen.errorMessage}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Composer */}
        <div className={`ws-composer ${isDragging ? "drop" : ""}`}>
          {draftImages.length > 0 && (
            <div className="ws-thumbstrip">
              {draftImages.map((src, i) => (
                <div key={i} className="ws-thumb">
                  <img src={src} alt="" />
                  <button
                    onClick={() =>
                      setDraftImages((prev) => prev.filter((_, j) => j !== i))
                    }
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
              {draftImages.length < 6 && (
                <button className="ws-thumb-add" onClick={onUploadClick}>
                  <Icon name="plus" size={20} />
                </button>
              )}
            </div>
          )}

          {/* Generation prefs — style + duration as compact native <select>
              dropdowns (no popover dep, keyboard-accessible), plus the binary
              auto-enrich toggle as a chip. Per-prompt; defaults match the
              backend defaults so the first generation behaves the same as
              before this picker existed. */}
          <div className="ws-genprefs">
            <label className="ws-genprefs-pill">
              <span className="ws-genprefs-pill-label">Style</span>
              <span className="ws-genprefs-pill-value">
                {STYLE_OPTIONS.find((s) => s.id === style)?.label ?? style}
              </span>
              <span className="ws-genprefs-caret">▾</span>
              <select
                className="ws-genprefs-select"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                aria-label="Style"
              >
                {STYLE_OPTIONS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="ws-genprefs-pill">
              <span className="ws-genprefs-pill-label">Length</span>
              <span className="ws-genprefs-pill-value">{duration}s</span>
              <span className="ws-genprefs-caret">▾</span>
              <select
                className="ws-genprefs-select"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                aria-label="Length"
              >
                {DURATION_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}s
                  </option>
                ))}
              </select>
            </label>
            <button
              className={`ws-genprefs-chip ${extendPrompt ? "on" : ""}`}
              onClick={() => setExtendPrompt((v) => !v)}
              type="button"
              title="Let Mim rewrite your prompt for richer visuals. Off → send exactly what you typed."
            >
              <Icon name="sparkle" size={12} />
              Auto-enrich {extendPrompt ? "on" : "off"}
            </button>
          </div>

          <div className="ws-composer-row">
            <button
              className="ws-iconbtn"
              onClick={onUploadClick}
              title="Add reference images"
            >
              <Icon name="plus" size={18} />
            </button>
            <textarea
              ref={composerRef}
              className="ws-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              placeholder={
                activeChild
                  ? `Describe a cartoon for ${activeChild.name}…  (Enter to send, Shift+Enter for newline)`
                  : "Describe a cartoon for your child…  (Enter to send)"
              }
              rows={1}
            />
            <button
              className="ws-sendbtn"
              onClick={send}
              disabled={!draft.trim() && draftImages.length === 0}
              title="Generate"
            >
              <Icon name="sparkle" size={16} />
              <span>Generate</span>
            </button>
          </div>
          <div className="ws-composer-hint">
            <span>
              Reference images guide the art style. Note: photos of people
              aren't used as likenesses — the generator only takes style cues.
            </span>
            <span className="ws-cost">
              <Icon name="star" size={12} /> 1 generation · $15
            </span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={onFileChange}
          />
        </div>
      </div>

      {/* Right: generated library */}
      <aside className="ws-library">
        <div
          className="ws-lib-head"
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <div className="ws-lib-title">Your family videos</div>
          <button
            onClick={() => videoInputRef.current?.click()}
            disabled={uploadingVideo}
            title="Upload your own video, or an audio file for a bedtime (audio-only) story"
            style={{
              marginLeft: "auto",
              border: "1px solid var(--line-strong)",
              background: "var(--surface)",
              color: "var(--ink)",
              borderRadius: 999,
              padding: "7px 14px",
              fontSize: 13,
              fontWeight: 800,
              fontFamily: "inherit",
              cursor: uploadingVideo ? "default" : "pointer",
              opacity: uploadingVideo ? 0.6 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="plus" size={14} />
            {uploadingVideo ? "Uploading…" : "Upload video or audio"}
          </button>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*,audio/*"
            style={{ display: "none" }}
            onChange={onVideoSelected}
          />
        </div>
        {generations.filter((g) => !dismissedFailedIds.has(g.id)).length === 0 ? (
          <div className="ws-lib-empty">
            <WebMascot mood="happy" color="#FF8A4C" size={80} />
            <div className="ws-lib-empty-h">No videos yet</div>
            <div className="ws-lib-empty-p">
              Your generated cartoons will live here.
            </div>
          </div>
        ) : (
          <div className="ws-lib-list">
            {generations
              .filter((g) => !dismissedFailedIds.has(g.id))
              .map((g) => {
              const pubNames = (g.publications ?? [])
                .map(
                  (p) => publishableKids.find((k) => k.id === p.childId)?.name,
                )
                .filter((n): n is string => Boolean(n));
              return (
                <div
                  key={g.id}
                  className={`ws-lib-row ${g.status !== "done" ? "loading" : ""}`}
                >
                  <button
                    className="ws-lib-row-main"
                    onClick={() => {
                      if (g.status !== "done") return;
                      dismissedGensRef.current.delete(g.id);
                      setOpenGen(g.id);
                    }}
                  >
                    <div
                      className="ws-lib-thumb"
                      style={{
                        background:
                          g.status === "done"
                            ? `linear-gradient(135deg, ${g.thumbColor ?? "#FFB774"}, #FF8A4C)`
                            : "var(--bg-warm)",
                        position: "relative",
                      }}
                    >
                      {g.status === "done" ? (
                        <WebMascot mood="cheer" color="#fff" size={28} />
                      ) : (
                        <div className="ws-spinner" />
                      )}
                      {g.status === "done" && g.thumbUrl && (
                        <img
                          src={
                            g.thumbUrl.startsWith("http")
                              ? g.thumbUrl
                              : `${getApiBaseUrl()}${g.thumbUrl}`
                          }
                          alt=""
                          onError={(e) => {
                            // Hide the broken poster so the gradient +
                            // mascot fallback remains visible.
                            (e.currentTarget as HTMLImageElement).style.display =
                              "none";
                          }}
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                    </div>
                    <div className="ws-lib-info">
                      <div className="ws-lib-row-title">
                        {prettyTitle(g.title) ||
                          (g.status === "queued" ? "Queued…" : "In progress…")}
                      </div>
                      <div className="ws-lib-row-sub">
                        {g.status === "done"
                          ? `${fmtTime(g.durationSec ?? 15)} · Custom`
                          : `${Math.round(STATUS_RATIO[g.status] * 100)}% · ${GEN_STATUS_LABEL[g.status]}`}
                      </div>
                      {g.status === "done" && pubNames.length > 0 && (
                        <div className="ws-lib-pub-badge">
                          <Icon name="sparkle" size={11} />
                          <span>Published for {pubNames.join(", ")}</span>
                        </div>
                      )}
                      {/* Review state. Lets the admin spot what's waiting on
                          them, and tells the parent where their request got to. */}
                      {g.status === "done" &&
                        !g.isPublic &&
                        g.reviewStatus === "pending" && (
                          <div className="ws-lib-pub-badge">
                            <Icon name="sparkle" size={11} />
                            <span>
                              {isAdmin
                                ? "Waiting for your review"
                                : "Waiting for review"}
                            </span>
                          </div>
                        )}
                      {g.status === "done" && g.reviewStatus === "rejected" && (
                        <div className="ws-lib-pub-badge">
                          <span>
                            Not approved
                            {g.reviewNote ? ` · ${g.reviewNote}` : ""}
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                  {!TERMINAL_STATUSES.includes(g.status) && (
                    <button
                      className="ws-lib-pub-cta"
                      disabled={cancellingJobId === g.jobId}
                      title="Stop this generation"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelGeneration(g.jobId);
                      }}
                    >
                      <span>
                        {cancellingJobId === g.jobId ? "Cancelling…" : "Cancel"}
                      </span>
                    </button>
                  )}
                  {(g.status === "failed" || g.status === "cancelled") && (
                    <button
                      className="ws-lib-pub-cta"
                      title="Remove this from your library"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissFailedCard(g.id);
                      }}
                    >
                      <span>Delete</span>
                    </button>
                  )}
                  {g.status === "done" && publishableKids.length > 0 && (
                    <button
                      className="ws-lib-pub-cta"
                      title={
                        pubNames.length > 0
                          ? "Edit publishing"
                          : "Publish for a kid"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setPublishGenId(g.id);
                      }}
                    >
                      <Icon name="plus" size={14} />
                      <span>
                        {pubNames.length > 0 ? "Edit" : "Publish"}
                      </span>
                    </button>
                  )}
                  {g.status === "done" &&
                    (isAdmin ? (
                      // Admin: their click IS the review decision.
                      <button
                        className={
                          "ws-lib-pub-cta" + (g.isPublic ? " is-public" : "")
                        }
                        disabled={publicBusyId === g.id}
                        title={
                          g.isPublic
                            ? "In the free library — anyone can watch. Click to make private."
                            : "Approve for the free library so other families can watch."
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePublic(g);
                        }}
                      >
                        <Icon
                          name={g.isPublic ? "sparkle" : "plus"}
                          size={14}
                        />
                        <span>{g.isPublic ? "Public" : "Approve"}</span>
                      </button>
                    ) : g.isPublic ? (
                      // Approved — live in the library. Nothing left to do.
                      <span
                        className="ws-lib-pub-cta is-public"
                        title="An admin approved this — other families can watch it."
                      >
                        <Icon name="sparkle" size={14} />
                        <span>Public</span>
                      </span>
                    ) : (
                      // Parent: submit / withdraw, with the pending + rejected
                      // states surfaced so the click never silently no-ops.
                      <button
                        className="ws-lib-pub-cta"
                        disabled={publicBusyId === g.id}
                        title={
                          g.reviewStatus === "pending"
                            ? "Waiting for an admin to review it. Click to take the request back."
                            : g.reviewStatus === "rejected"
                              ? `Not approved${g.reviewNote ? `: ${g.reviewNote}` : ""}. You can edit it and ask again.`
                              : "Ask Kidly to review this so other families can watch. It stays private until approved."
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleReview(g);
                        }}
                      >
                        <Icon
                          name={
                            g.reviewStatus === "pending" ? "sparkle" : "plus"
                          }
                          size={14}
                        />
                        <span>
                          {g.reviewStatus === "pending"
                            ? "In review"
                            : g.reviewStatus === "rejected"
                              ? "Not approved"
                              : "Share publicly"}
                        </span>
                      </button>
                    ))}
                  {g.status === "done" && (
                    <button
                      className="ws-lib-icon-btn"
                      aria-label="Archive video"
                      title="Archive — hides it everywhere; permanently deleted in 10 days (restorable until then)"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArchive(g.id);
                      }}
                    >
                      <Icon name="archive" size={15} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </aside>

      {/* Post-generation details editor. Auto-opens when a generation
          transitions to `done` (see applyServerUpdate); also opens when
          the parent clicks a finished card. */}
      {currentGen && currentGen.status === "done" && (
        <WebVideoDetailsEditor
          key={currentGen.id}
          videoId={currentGen.id}
          initialTitle={currentGen.title ?? ""}
          initialDescription={currentGen.description ?? null}
          initialCategories={currentGen.categories ?? []}
          initialAgeMin={currentGen.ageMin ?? 2}
          initialAgeMax={currentGen.ageMax ?? 8}
          initialThumbUrl={currentGen.thumbUrl ?? ""}
          videoUrl={currentGen.videoUrl}
          videoDurationSec={currentGen.durationSec}
          onClose={() => {
            // Remember this dismissal so the polling auto-open can't
            // immediately bounce the modal back open.
            dismissedGensRef.current.add(currentGen.id);
            setOpenGen(null);
          }}
          onApply={(updated) => {
            // Merge the PATCH response back into the local generations
            // state. backendToGeneration carries thumbColor forward via
            // the spread, so the card art stays stable.
            const next = backendToGeneration(updated);
            setGenerations((prev) =>
              prev.map((g) =>
                g.id === updated.id
                  ? { ...g, ...next, thumbColor: g.thumbColor ?? next.thumbColor }
                  : g,
              ),
            );
          }}
          onPlay={() => {
            if (!onPlay) return;
            onPlay({
              id: currentGen.id,
              title: currentGen.title ?? "Custom cartoon",
              category: "Custom",
              duration: currentGen.durationSec ?? 15,
              ageMin: currentGen.ageMin ?? 2,
              ageMax: currentGen.ageMax ?? 8,
              isPremium: false,
            });
            dismissedGensRef.current.add(currentGen.id);
            setOpenGen(null);
          }}
          onEditQuestions={() => setQuestionsGenId(currentGen.id)}
          onIterate={() => {
            // Seed the composer with an "iterate on this" prompt the
            // parent can edit. Doesn't auto-submit so they can tweak.
            const iterateTitle = currentGen.title ?? currentGen.prompt;
            const seeded = `Like "${iterateTitle}", but `;
            setDraft(seeded);
            // Drop a chat message so the parent gets visible feedback that
            // the Iterate button did something — otherwise the modal just
            // closes and the seeded composer text is easy to miss.
            setMessages((prev) => [
              ...prev,
              {
                id: uid(),
                role: "assistant",
                text: `I started a variation of "${iterateTitle}". Finish the sentence below and hit send.`,
              },
            ]);
            dismissedGensRef.current.add(currentGen.id);
            setOpenGen(null);
            // Defer focus + scroll until after the modal unmounts and
            // React commits the new draft value — otherwise the modal
            // is still mounted and steals focus back. setTimeout(0) is
            // more reliable than a single RAF here because the modal's
            // unmount + composer's controlled value update span more
            // than one frame on slower machines.
            setTimeout(() => {
              const el = composerRef.current;
              if (!el) return;
              el.scrollIntoView({ block: "end" });
              el.focus();
              // Place caret at the end of the seeded text so the parent
              // can type immediately without a click.
              try {
                el.setSelectionRange(seeded.length, seeded.length);
              } catch {
                // Some browsers reject setSelectionRange on a non-focused
                // input — focus first, ignore the throw if it still bites.
              }
            }, 0);
          }}
        />
      )}

      {/* Publish-for-kids modal. Lives outside the gen-card map so closing
          it doesn't unmount the cards underneath. */}
      {publishGen && (
        <PublishForKidsModal
          key={publishGen.id}
          generation={publishGen}
          kids={publishableKids}
          token={accessToken ?? ""}
          onClose={() => setPublishGenId(null)}
          onApply={(publications) => {
            applyPublications(publishGen.id, publications);
          }}
        />
      )}

      {/* Question editor for a finished generation. Rendered last so it
          stacks above the preview modal — closing it returns the parent to
          the preview rather than the gen-card list. */}
      {questionsGen && (
        <WebQuestionEditor
          key={questionsGen.id}
          videoId={questionsGen.id}
          videoTitle={questionsGen.title}
          videoDurationSec={questionsGen.durationSec}
          // Used by the "Frames from video" picker tab — pulled into a
          // hidden <video> element for canvas frame-extraction. Optional;
          // the picker degrades to the Upload-only tab when missing.
          videoUrl={questionsGen.videoUrl}
          // Parent-uploaded videos have no Wan stills → the editor's image
          // picker offers Upload only (no "Frames from video" tab).
          isUploaded={questionsGen.isUploaded}
          onClose={() => setQuestionsGenId(null)}
        />
      )}


      <StudioStyles />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PublishForKidsModal — pick which of the parent's kids see this video.
//
// State machine is intentionally local: the modal opens with a snapshot of
// the generation's current publications, the parent toggles a checkbox set,
// and on save we diff against the snapshot:
//   - newly checked → publishVideo(toAdd)
//   - newly unchecked → unpublishVideo(toRemove)
// The two calls run sequentially so a single 4xx surfaces without partially
// mutating the join. Result of whichever call ran last is propagated to the
// parent via `onApply` so the gen-card badge updates instantly.
// ─────────────────────────────────────────────────────────────────────────
function PublishForKidsModal({
  generation,
  kids,
  token,
  onClose,
  onApply,
}: {
  generation: Generation;
  kids: ChildWithProgress[];
  token: string;
  onClose: () => void;
  onApply: (publications: VideoPublication[]) => void;
}) {
  // Initial selection — kids this video was already published for.
  const initial = useMemo(
    () => new Set((generation.publications ?? []).map((p) => p.childId)),
    [generation.publications],
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Hasn't-changed short-circuit so the Save button is disabled when there's
  // nothing to do — keeps the parent from firing no-op POSTs.
  const isDirty = useMemo(() => {
    if (selected.size !== initial.size) return true;
    for (const id of selected) if (!initial.has(id)) return true;
    return false;
  }, [selected, initial]);

  const save = useCallback(async () => {
    if (!token) {
      setError("You're signed out — please sign in again.");
      return;
    }
    const toAdd: string[] = [];
    const toRemove: string[] = [];
    for (const id of selected) if (!initial.has(id)) toAdd.push(id);
    for (const id of initial) if (!selected.has(id)) toRemove.push(id);
    if (toAdd.length === 0 && toRemove.length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let latest: VideoPublication[] | undefined;
      // Publish first so the kid sees the new row even if the unpublish step
      // races on slow networks (no UX loss either way).
      if (toAdd.length > 0) {
        const res = await adminContentApi.publishVideo(
          generation.id,
          toAdd,
          token,
        );
        latest = res.publications ?? [];
      }
      if (toRemove.length > 0) {
        const res = await adminContentApi.unpublishVideo(
          generation.id,
          toRemove,
          token,
        );
        latest = res.publications ?? [];
      }
      if (latest) onApply(latest);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  }, [token, selected, initial, generation.id, onApply, onClose]);

  return (
    <div className="ws-modal" onClick={onClose}>
      <div
        className="ws-modal-card ws-pub-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="ws-modal-close" onClick={onClose}>
          ×
        </button>
        <div className="ws-pub-head">
          <div className="ws-pub-eyebrow">PUBLISH FOR…</div>
          <div className="ws-pub-title">
            {prettyTitle(generation.title) || "Custom cartoon"}
          </div>
          <div className="ws-pub-sub">
            Pick which of your kids should see this in their library.
          </div>
        </div>
        {kids.length === 0 ? (
          <div className="ws-pub-empty">
            Add a child first to publish videos to their library.
          </div>
        ) : (
          <div className="ws-pub-list">
            {kids.map((k) => {
              const checked = selected.has(k.id);
              return (
                <label
                  key={k.id}
                  className={`ws-pub-row ${checked ? "checked" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(k.id)}
                  />
                  <div
                    className="ws-pub-avatar"
                    style={{ background: k.color ?? "#FFB774" }}
                  >
                    {k.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="ws-pub-row-text">
                    <div className="ws-pub-row-name">{k.name}</div>
                    <div className="ws-pub-row-sub">
                      Age {k.age}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
        {error && <div className="ws-pub-error">{error}</div>}
        <div className="ws-pub-actions">
          <button
            className="btn btn-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn"
            onClick={save}
            disabled={saving || !isDirty || kids.length === 0}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Minimal markdown: **bold** only. Avoids a library dep.
function renderMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

// Inlined styles so Studio is self-contained — no edits to web/styles.css.
function StudioStyles() {
  return (
    <style>{`
      .ws-root {
        display: grid;
        grid-template-columns: 1fr 340px;
        /* Explicit single row that FILLS and CLAMPS to the shell height.
           Without this the implicit row is auto-sized and grows to the tall
           chat thread, overflowing height:100% and pushing the whole grid --
           the "Your family videos" column included -- into .w-main's page
           scroll. minmax(0,1fr) + overflow:hidden keeps the row at viewport
           height so each column scrolls INTERNALLY (.ws-scroller /
           .ws-lib-list) and the library header stays pinned. */
        grid-template-rows: minmax(0, 1fr);
        gap: 0;
        height: 100%;
        min-height: 0;
        overflow: hidden;
        position: relative; /* anchor for the absolute sidebar overlay */
      }

      /* Backdrop dim while the sidebar is open. Sits above the chat /
         library content but below the sliding sidebar itself. */
      .ws-sessions-backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0,0,0,0.25);
        z-index: 20;
        animation: ws-fade-in 160ms ease-out;
      }
      @keyframes ws-fade-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }

      /* Sessions sidebar — absolute overlay sliding in from the left.
         Hidden by default (translateX(-100%)); .open class slides it in. */
      .ws-sessions {
        position: absolute;
        top: 0; bottom: 0; left: 0;
        width: 280px;
        background: var(--surface);
        border-right: 1px solid var(--line);
        box-shadow: 8px 0 24px rgba(0,0,0,0.10);
        display: flex; flex-direction: column;
        z-index: 21;
        transform: translateX(-100%);
        transition: transform 220ms cubic-bezier(.2,.8,.2,1);
        will-change: transform;
      }
      .ws-sessions.open { transform: translateX(0); }
      .ws-sessions-head {
        display: flex; align-items: center; justify-content: space-between;
        padding: 14px 14px 4px;
      }
      .ws-sessions-title {
        font-family: var(--display);
        font-size: 16px; font-weight: 900;
        color: var(--ink);
      }
      .ws-sessions-close {
        width: 28px; height: 28px;
        border-radius: 50%;
        border: 1.5px solid var(--line);
        background: var(--surface);
        color: var(--ink-2);
        cursor: pointer;
        font: inherit; font-weight: 900; font-size: 16px;
        line-height: 1;
      }
      .ws-sessions-close:hover { color: var(--ink); border-color: var(--ink); }
      .ws-sessions-new {
        display: inline-flex; align-items: center; gap: 6px;
        margin: 8px 14px 6px;
        padding: 9px 14px;
        background: var(--bg-warm);
        color: var(--primary-ink);
        border: 0; border-radius: 999px;
        font: inherit; font-weight: 800; font-size: 13px;
        cursor: pointer;
      }
      .ws-sessions-new:hover { background: #FFDDB8; }
      .ws-sessions-list {
        flex: 1; min-height: 0;
        overflow-y: auto;
        padding: 0 8px 14px;
        display: flex; flex-direction: column; gap: 2px;
      }
      .ws-sessions-empty {
        padding: 14px;
        text-align: center;
        font-size: 12px; font-weight: 700;
        color: var(--ink-3);
      }
      .ws-session-row {
        display: flex; align-items: center; gap: 4px;
        border-radius: 10px;
        padding: 2px;
      }
      .ws-session-row:hover { background: var(--bg-warm); }
      .ws-session-row.active { background: var(--bg-warm); }
      .ws-session-main {
        flex: 1; min-width: 0;
        text-align: left;
        background: transparent; border: 0;
        cursor: pointer;
        padding: 6px 10px;
        font: inherit; color: inherit;
      }
      .ws-session-title {
        font-size: 13px; font-weight: 800;
        color: var(--ink);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .ws-session-sub {
        font-size: 11px; font-weight: 700;
        color: var(--ink-3);
        margin-top: 2px;
      }
      .ws-session-actions {
        display: none;
        gap: 2px;
        flex: 0 0 auto;
        padding-right: 4px;
      }
      .ws-session-row:hover .ws-session-actions,
      .ws-session-row.active .ws-session-actions {
        display: flex;
      }
      .ws-session-act {
        width: 24px; height: 24px;
        border-radius: 50%;
        border: 0;
        background: transparent;
        color: var(--ink-2);
        cursor: pointer;
        font: inherit; font-weight: 800; font-size: 13px;
        line-height: 1;
      }
      .ws-session-act:hover {
        background: rgba(0,0,0,0.06);
        color: var(--ink);
      }
      .ws-session-del:hover { color: #C44; }

      /* Hamburger button in the chat header. */
      .ws-header-left {
        display: flex; align-items: center; gap: 10px;
      }
      .ws-hamburger {
        width: 36px; height: 36px;
        border-radius: 10px;
        border: 0;
        background: var(--bg-warm);
        color: var(--primary-ink);
        cursor: pointer;
        display: grid; place-items: center;
      }
      .ws-hamburger:hover { background: #FFDDB8; }
      .ws-thread {
        display: flex;
        flex-direction: column;
        height: 100%;
        border-right: 1px solid var(--line);
        min-width: 0;
      }
      .ws-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 22px 28px 16px;
        border-bottom: 1px solid var(--line);
        background: var(--surface);
      }
      .ws-eyebrow {
        font-size: 11px; font-weight: 800;
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--ink-2);
      }
      .ws-title {
        font-family: var(--display);
        font-size: 22px; font-weight: 900;
        color: var(--ink); letter-spacing: -0.01em;
        margin-top: 2px;
      }
      .ws-newbtn {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 8px 14px; border-radius: 999px;
        background: var(--bg-warm); color: var(--primary-ink);
        border: 0; font: inherit; font-weight: 800; font-size: 13px;
        cursor: pointer;
      }
      .ws-newbtn:hover { background: #FFDDB8; }

      .ws-scroller {
        flex: 1; min-height: 0;
        overflow-y: auto;
        padding: 24px 28px 12px;
        display: flex; flex-direction: column; gap: 14px;
      }
      .ws-scroller::-webkit-scrollbar { width: 10px; }
      .ws-scroller::-webkit-scrollbar-thumb {
        background: rgba(46,35,22,0.15); border-radius: 6px;
      }

      .ws-genprefs {
        display: flex; flex-wrap: wrap; gap: 8px;
        padding: 8px 12px 6px;
        align-items: center;
      }
      /* "Pill" wraps the native select so we can paint a chip-shaped
          container around it without losing the OS dropdown behavior. */
      .ws-genprefs-pill {
        position: relative;
        display: inline-flex; align-items: center;
        height: 28px; padding: 0 24px 0 10px;
        border-radius: 999px;
        border: 1px solid var(--line);
        background: var(--surface);
        cursor: pointer;
        transition: border-color 120ms, background-color 120ms;
      }
      .ws-genprefs-pill:hover {
        border-color: var(--line-strong);
        background: var(--bg-warm);
      }
      .ws-genprefs-pill-label {
        font-size: 10px; font-weight: 800; letter-spacing: 0.1em;
        text-transform: uppercase; color: var(--ink-3);
        margin-right: 6px;
        pointer-events: none;
      }
      .ws-genprefs-pill-value {
        font-size: 12px; font-weight: 700;
        color: var(--ink);
        pointer-events: none;
      }
      /* The native <select> covers the whole pill but is invisible — that
         way clicks anywhere on the pill open the dropdown, not just on
         the value text. Visible label/value/caret sit on top with
         pointer-events: none so they don't intercept the click. */
      .ws-genprefs-select {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        appearance: none;
        -webkit-appearance: none;
        border: 0;
        background: transparent;
        cursor: pointer;
        padding: 0;
        margin: 0;
        font: inherit;
      }
      .ws-genprefs-caret {
        position: absolute; right: 9px;
        font-size: 10px;
        color: var(--ink-3);
        pointer-events: none;
      }
      .ws-genprefs-chip {
        display: inline-flex; align-items: center; gap: 4px;
        height: 28px; padding: 0 12px;
        border-radius: 999px;
        border: 1px solid var(--line);
        background: var(--surface);
        color: var(--ink-2);
        font-size: 12px; font-weight: 700;
        cursor: pointer;
        transition: background-color 120ms, color 120ms, border-color 120ms;
      }
      .ws-genprefs-chip:hover {
        border-color: var(--line-strong);
      }
      .ws-genprefs-chip.on {
        background: var(--ink);
        color: #fff;
        border-color: var(--ink);
      }
      .ws-starters {
        display: flex; flex-wrap: wrap; gap: 8px;
        margin: 10px 0 0;
      }
      .ws-starter {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 8px 12px; border-radius: 999px;
        background: var(--surface); border: 1px solid var(--line);
        color: var(--ink); font: inherit; font-weight: 700; font-size: 13px;
        cursor: pointer;
      }
      .ws-starter:hover { background: var(--bg-warm); }

      .ws-msg {
        display: flex; gap: 12px;
        max-width: 760px;
        margin: 4px 0;
      }
      .ws-msg-user {
        margin-left: auto; flex-direction: row-reverse;
      }
      .ws-avatar {
        width: 36px; height: 36px; border-radius: 50%;
        background: var(--bg-warm);
        display: grid; place-items: center;
        flex: 0 0 auto;
        overflow: hidden;
      }
      .ws-bubble {
        padding: 12px 16px;
        border-radius: 16px;
        font-size: 15px; font-weight: 600;
        color: var(--ink); line-height: 1.45;
        background: var(--surface);
        border: 1px solid var(--line);
        max-width: 620px;
      }
      .ws-msg-user .ws-bubble {
        background: var(--primary);
        color: #fff;
        border-color: var(--primary);
      }
      .ws-msg-user .ws-bubble strong { color: #fff; }

      .ws-imgs {
        display: flex; flex-wrap: wrap; gap: 8px;
        margin-bottom: 10px;
      }
      .ws-imgs img {
        width: 88px; height: 88px;
        object-fit: cover;
        border-radius: 12px;
        border: 2px solid rgba(255,255,255,0.6);
      }
      .ws-text strong { font-weight: 900; }

      .ws-progress {
        margin-top: 10px;
        padding: 10px 12px;
        border-radius: 10px;
        background: var(--bg-warm);
        color: var(--primary-ink);
      }
      .ws-progress-row {
        display: inline-flex; align-items: center; gap: 8px;
        font-size: 13px; font-weight: 800;
      }
      .ws-progress-bg {
        height: 4px; border-radius: 4px;
        background: rgba(180,90,36,0.18); overflow: hidden;
        margin-top: 6px;
      }
      .ws-progress-fill {
        height: 100%;
        background: var(--primary);
        transition: width 600ms ease;
      }

      .ws-gen-card {
        margin-top: 10px;
        display: flex; align-items: center; gap: 14px;
        padding: 10px 14px;
        border-radius: 14px;
        background: linear-gradient(135deg, #FFE8D1, #FFF4E6);
        border: 1px solid #F0DCB8;
        color: var(--ink);
        font: inherit;
        cursor: pointer;
        text-align: left;
        width: 100%;
      }
      .ws-gen-card:hover { filter: brightness(1.02); }
      .ws-gen-thumb {
        width: 56px; height: 56px; border-radius: 12px;
        display: grid; place-items: center;
        flex: 0 0 auto;
      }
      .ws-gen-meta { flex: 1; min-width: 0; }
      .ws-gen-cat {
        font-size: 10px; font-weight: 900; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--primary-ink);
      }
      .ws-gen-title {
        font-family: var(--display);
        font-size: 16px; font-weight: 900;
        color: var(--ink); margin-top: 2px;
      }
      .ws-gen-sub {
        font-size: 12px; font-weight: 700; color: var(--ink-2);
        margin-top: 2px;
      }

      /* Composer */
      .ws-composer {
        border-top: 1px solid var(--line);
        padding: 16px 28px 20px;
        background: var(--surface);
        transition: background-color 160ms;
      }
      .ws-composer.drop {
        background: var(--bg-warm);
        outline: 2px dashed var(--primary);
        outline-offset: -16px;
      }
      .ws-thumbstrip {
        display: flex; gap: 8px; margin-bottom: 10px;
        flex-wrap: wrap;
      }
      .ws-thumb {
        position: relative;
        width: 64px; height: 64px;
      }
      .ws-thumb img {
        width: 100%; height: 100%;
        object-fit: cover; border-radius: 10px;
        border: 1px solid var(--line);
      }
      .ws-thumb button {
        position: absolute; top: -6px; right: -6px;
        width: 22px; height: 22px;
        border-radius: 50%; border: 0;
        background: var(--ink); color: #fff;
        font: inherit; font-weight: 800; font-size: 14px;
        cursor: pointer;
        line-height: 1;
      }
      .ws-thumb-add {
        width: 64px; height: 64px;
        border-radius: 10px;
        border: 1.5px dashed var(--line-strong);
        background: transparent;
        cursor: pointer;
        color: var(--ink-2);
        display: grid; place-items: center;
      }
      .ws-thumb-add:hover { background: var(--bg-warm); color: var(--ink); }

      .ws-composer-row {
        display: flex; align-items: center; gap: 10px;
      }
      .ws-iconbtn {
        width: 40px; height: 40px;
        border-radius: 12px;
        border: 1px solid var(--line);
        background: var(--surface);
        color: var(--ink-2);
        cursor: pointer;
        display: grid; place-items: center;
        flex: 0 0 auto;
      }
      .ws-iconbtn:hover { background: var(--bg-warm); color: var(--ink); }
      .ws-input {
        flex: 1;
        min-height: 44px; max-height: 200px;
        padding: 12px 14px;
        border: 1.5px solid var(--line-strong);
        border-radius: 14px;
        font: inherit; font-size: 15px; font-weight: 600;
        color: var(--ink);
        background: var(--surface);
        outline: none;
        resize: none;
        box-sizing: border-box;
        line-height: 1.4;
      }
      .ws-input:focus {
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(255,138,76,0.18);
      }
      .ws-sendbtn {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 12px 18px;
        border-radius: 14px;
        background: var(--primary);
        color: #fff; border: 0;
        font: inherit; font-weight: 800; font-size: 14px;
        cursor: pointer;
        box-shadow: inset 0 -3px 0 rgba(0,0,0,0.12);
      }
      .ws-sendbtn:disabled { opacity: 0.4; cursor: not-allowed; }
      .ws-composer-hint {
        display: flex; justify-content: space-between; align-items: center;
        margin-top: 8px;
        font-size: 12px; font-weight: 600; color: var(--ink-3);
      }
      .ws-cost {
        display: inline-flex; align-items: center; gap: 4px;
        color: var(--primary-ink); font-weight: 800;
      }

      /* Library sidebar */
      .ws-library {
        display: flex; flex-direction: column;
        background: var(--surface);
        height: 100%;
        min-width: 0;
      }
      .ws-lib-head {
        padding: 22px 20px 14px;
        border-bottom: 1px solid var(--line);
      }
      .ws-lib-title {
        font-family: var(--display);
        font-weight: 900; font-size: 18px;
        color: var(--ink);
      }
      .ws-lib-count {
        font-size: 12px; font-weight: 700; color: var(--ink-3);
        margin-top: 2px;
      }
      .ws-lib-empty {
        flex: 1;
        display: grid; place-items: center; gap: 6px;
        padding: 40px 20px; text-align: center;
        color: var(--ink-3);
      }
      .ws-lib-empty-h {
        font-family: var(--display);
        font-weight: 900; font-size: 16px; color: var(--ink);
        margin-top: 8px;
      }
      .ws-lib-empty-p { font-size: 13px; font-weight: 600; }
      .ws-lib-list {
        flex: 1; min-height: 0;
        overflow-y: auto;
        padding: 8px;
      }
      /* The lib row used to BE a <button>. We split it into a wrapper +
         inner action button so the "Publish for…" CTA can live alongside
         the open-preview affordance without nesting buttons. */
      .ws-lib-row {
        width: 100%;
        display: flex; align-items: center; gap: 6px;
        padding: 6px 8px;
        border-radius: 12px;
      }
      .ws-lib-row:hover { background: var(--bg-warm); }
      .ws-lib-row.loading { cursor: progress; }
      .ws-lib-row-main {
        flex: 1; min-width: 0;
        display: flex; align-items: center; gap: 12px;
        padding: 4px 4px;
        background: transparent; border: 0;
        cursor: pointer;
        text-align: left;
        font: inherit;
        color: inherit;
      }
      .ws-lib-pub-badge {
        margin-top: 4px;
        display: inline-flex; align-items: center; gap: 4px;
        font-size: 10.5px; font-weight: 800;
        color: var(--primary-ink);
        background: var(--bg-warm);
        padding: 2px 6px;
        border-radius: 999px;
        max-width: 100%;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .ws-lib-pub-cta {
        display: inline-flex; align-items: center; gap: 4px;
        padding: 6px 10px;
        background: var(--bg-warm);
        color: var(--primary-ink);
        border: 0; border-radius: 999px;
        font: inherit; font-weight: 800; font-size: 11.5px;
        cursor: pointer;
        flex: 0 0 auto;
      }
      .ws-lib-pub-cta:hover { background: #FFDDB8; }
      .ws-lib-pub-cta:disabled { opacity: 0.55; cursor: default; }
      /* Active "in the free library" state — filled so it reads as ON. */
      .ws-lib-pub-cta.is-public { background: var(--primary); color: #fff; }
      .ws-lib-pub-cta.is-public:hover { background: var(--primary); filter: brightness(0.96); }
      /* Archive — a quiet, destructive trailing action. Deliberately NOT a
         warm pill like Publish / Make-public so it never reads as a primary
         CTA: muted icon by default, soft-red on hover. */
      .ws-lib-icon-btn {
        display: inline-flex; align-items: center; justify-content: center;
        width: 30px; height: 30px;
        margin-left: 2px;
        background: transparent;
        color: var(--ink-3);
        border: 0; border-radius: 999px;
        cursor: pointer;
        flex: 0 0 auto;
      }
      .ws-lib-icon-btn:hover { background: #FCE3E0; color: #C0392B; }
      .ws-lib-icon-btn:disabled { opacity: 0.5; cursor: default; }
      .ws-lib-thumb {
        width: 56px; height: 36px;
        border-radius: 8px;
        display: grid; place-items: center;
        flex: 0 0 auto;
        overflow: hidden;
      }
      .ws-lib-info { flex: 1; min-width: 0; }
      .ws-lib-row-title {
        font-size: 13px; font-weight: 800; color: var(--ink);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .ws-lib-row-sub {
        font-size: 11px; font-weight: 700; color: var(--ink-3);
        margin-top: 2px;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .ws-spinner {
        width: 18px; height: 18px;
        border: 2.5px solid rgba(180,90,36,0.18);
        border-top-color: var(--primary);
        border-radius: 50%;
        animation: ws-spin 700ms linear infinite;
      }
      @keyframes ws-spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }

      /* Preview modal.
         The scrolling here is load-bearing, not cosmetic. This used to be
         "display: grid; place-items: center" with no scrolling and no height
         cap: a card taller than the window overflowed equally off the TOP and
         bottom, putting .ws-modal-close (absolute, top: 12px) above the
         viewport with no way to scroll to it — the modal became genuinely
         impossible to dismiss. Because it depended on content height it came
         and went, which is why it kept resurfacing.
         Flex plus "margin: auto" keeps short cards centred while letting tall
         ones scroll from the top, so the close button is always reachable. */
      .ws-modal {
        position: fixed; inset: 0;
        background: rgba(0,0,0,0.45);
        display: flex; align-items: flex-start; justify-content: center;
        padding: 24px;
        overflow-y: auto;
        overscroll-behavior: contain;
        z-index: 100;
      }
      .ws-modal-card {
        width: min(640px, 100%);
        margin: auto;
        flex: 0 0 auto;
        background: var(--surface);
        border-radius: 22px;
        overflow: hidden;
        position: relative;
        box-shadow: 0 30px 60px rgba(0,0,0,0.35);
      }
      /* The question editor stacks ON TOP of the details editor. Both are
         .ws-modal, so relying on DOM order for that was fragile — make the
         layering explicit. */
      .ws-modal-stacked { z-index: 110; }
      .ws-modal-close {
        position: absolute; top: 12px; right: 12px;
        width: 34px; height: 34px; border-radius: 50%;
        border: 0; background: rgba(0,0,0,0.55); color: #fff;
        font: inherit; font-weight: 800; font-size: 18px;
        cursor: pointer; z-index: 1;
        line-height: 1;
      }
      .ws-modal-thumb {
        aspect-ratio: 16 / 9;
        display: grid; place-items: center;
      }
      .ws-modal-meta { padding: 20px 22px 22px; }
      .ws-modal-eyebrow {
        font-size: 11px; font-weight: 900; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--primary-ink);
      }
      .ws-modal-title {
        font-family: var(--display);
        font-size: 24px; font-weight: 900;
        color: var(--ink); margin-top: 4px;
      }
      .ws-modal-prompt {
        font-size: 14px; font-weight: 500; font-style: italic;
        color: var(--ink-2); margin: 8px 0 16px;
      }
      .ws-modal-row {
        display: flex; gap: 10px;
      }

      /* Publish-for-kids modal */
      .ws-pub-card {
        width: min(440px, 100%);
        padding: 24px 22px 18px;
      }
      .ws-pub-head { margin-bottom: 16px; }
      .ws-pub-eyebrow {
        font-size: 11px; font-weight: 900; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--primary-ink);
      }
      .ws-pub-title {
        font-family: var(--display);
        font-size: 22px; font-weight: 900;
        color: var(--ink); margin-top: 4px;
        /* Long upload filenames must wrap + clamp inside the card instead of
           spilling past the right edge or running under the × close button. */
        padding-right: 40px;
        overflow-wrap: anywhere;
        word-break: break-word;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .ws-pub-sub {
        font-size: 13px; font-weight: 600;
        color: var(--ink-2); margin-top: 4px;
      }
      .ws-pub-empty {
        font-size: 13px; font-weight: 600;
        color: var(--ink-2);
        text-align: center;
        padding: 24px 12px;
        border: 1px dashed var(--line);
        border-radius: 12px;
      }
      .ws-pub-list {
        display: flex; flex-direction: column; gap: 8px;
        margin-bottom: 14px;
        max-height: 320px;
        overflow-y: auto;
      }
      .ws-pub-row {
        display: flex; align-items: center; gap: 12px;
        padding: 10px 12px;
        border-radius: 12px;
        border: 1.5px solid var(--line);
        cursor: pointer;
        transition: border-color 120ms ease, background 120ms ease;
      }
      .ws-pub-row:hover { background: var(--bg-warm); }
      .ws-pub-row.checked {
        border-color: var(--primary);
        background: var(--bg-warm);
      }
      .ws-pub-row input[type="checkbox"] {
        width: 18px; height: 18px;
        accent-color: #FF8A4C;
        flex: 0 0 auto;
      }
      .ws-pub-avatar {
        width: 36px; height: 36px;
        border-radius: 50%;
        display: grid; place-items: center;
        color: #fff;
        font-weight: 900; font-size: 14px;
        flex: 0 0 auto;
      }
      .ws-pub-row-text { flex: 1; min-width: 0; }
      .ws-pub-row-name {
        font-size: 14px; font-weight: 800; color: var(--ink);
      }
      .ws-pub-row-sub {
        font-size: 11.5px; font-weight: 700; color: var(--ink-3);
        margin-top: 2px;
      }
      .ws-pub-error {
        font-size: 12.5px; font-weight: 700;
        color: #C0392B;
        background: rgba(192,57,43,0.08);
        padding: 8px 10px;
        border-radius: 8px;
        margin-bottom: 10px;
      }
      .ws-pub-actions {
        display: flex; justify-content: flex-end; gap: 8px;
      }
    `}</style>
  );
}

export default WebStudio;
